import { useState, useEffect, useCallback } from 'react'
import { Modal, Image, ActionIcon, Group, Text, Box } from '@mantine/core'
import { IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react'

interface ImageLightboxProps {
  images: string[]
  initialIndex?: number
  opened: boolean
  onClose: () => void
}

export default function ImageLightbox({ images, initialIndex = 0, opened, onClose }: ImageLightboxProps) {
  const [current, setCurrent] = useState(initialIndex)

  useEffect(() => { setCurrent(initialIndex) }, [initialIndex, opened])

  const prev = useCallback(() => setCurrent(i => (i - 1 + images.length) % images.length), [images.length])
  const next = useCallback(() => setCurrent(i => (i + 1) % images.length), [images.length])

  useEffect(() => {
    if (!opened) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft')  prev()
      if (e.key === 'ArrowRight') next()
      if (e.key === 'Escape')     onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [opened, prev, next, onClose])

  if (!images.length) return null

  return (
    <Modal
      opened={opened} onClose={onClose}
      size="auto" centered withCloseButton={false}
      styles={{
        content: { background: 'rgba(0,0,0,0.92)', boxShadow: 'none', borderRadius: 12, padding: 0 },
        body: { padding: 0 },
        overlay: { backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.7)' },
      }}
    >
      <Box style={{ position: 'relative' }}>
        {/* Close button */}
        <ActionIcon
          onClick={onClose}
          style={{ position: 'absolute', top: 10, right: 10, zIndex: 10,
            background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}
          size="lg" radius="xl" variant="subtle" color="white">
          <IconX size={18} />
        </ActionIcon>

        {/* Image */}
        <Image
          src={images[current]}
          maw="90vw"
          mah="85vh"
          fit="contain"
          style={{ display: 'block', borderRadius: 12 }}
        />

        {/* Navigation */}
        {images.length > 1 && (
          <>
            <ActionIcon onClick={prev}
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}
              size="xl" radius="xl" variant="subtle" color="white">
              <IconChevronLeft size={22} />
            </ActionIcon>
            <ActionIcon onClick={next}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}
              size="xl" radius="xl" variant="subtle" color="white">
              <IconChevronRight size={22} />
            </ActionIcon>
          </>
        )}

        {/* Counter */}
        {images.length > 1 && (
          <Group justify="center" style={{ position: 'absolute', bottom: 12, width: '100%' }}>
            <Text size="xs" c="white" style={{ background: 'rgba(0,0,0,0.5)', padding: '2px 10px', borderRadius: 99 }}>
              {current + 1} / {images.length}
            </Text>
          </Group>
        )}
      </Box>
    </Modal>
  )
}

// Hook to manage lightbox state
export function useLightbox() {
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null)
  const open = (images: string[], index = 0) => setLightbox({ images, index })
  const close = () => setLightbox(null)
  return { lightbox, openLightbox: open, closeLightbox: close }
}
